// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Shared chrome for every view: the title block that states the question the
// view answers, stat tiles, legends, and a couple of tiny SVG helpers.

import { attr, esc } from '../format';

export function viewHead(opts: { title: string; question: string; blurb: string }): string {
  return `<header class="view-head">
    <h1>${opts.title}</h1>
    <p class="view-q">${opts.question}</p>
    <p class="view-blurb">${opts.blurb}</p>
  </header>`;
}

export interface Stat {
  label: string;
  value: string;
  sub?: string;
  tip?: string;
  tone?: 'good' | 'warn' | 'bad' | 'plain';
}

export function statRow(stats: Stat[]): string {
  return `<div class="stat-row">${stats
    .map(
      (s) => `<div class="stat ${s.tone ? `stat-${s.tone}` : ''}"${s.tip ? ` data-tip="${attr(s.tip)}"` : ''}>
        <span class="stat-v">${s.value}</span>
        <span class="stat-l">${s.label}</span>
        ${s.sub ? `<span class="stat-s">${s.sub}</span>` : ''}
      </div>`,
    )
    .join('')}</div>`;
}

export interface LegendItem {
  color: string;
  label: string;
  tip?: string;
  key?: string;
  hatch?: boolean;
}

export function legend(items: LegendItem[], opts: { interactive?: boolean } = {}): string {
  return `<div class="legend${opts.interactive ? ' legend-live' : ''}">${items
    .map(
      (i) =>
        `<${opts.interactive ? 'button type="button"' : 'span'} class="legend-item"${i.key ? ` data-legend="${attr(i.key)}"` : ''}${i.tip ? ` data-tip="${attr(i.tip)}"` : ''}>
          <span class="swatch${i.hatch ? ' swatch-hatch' : ''}" style="background:${i.color}"></span>${esc(i.label)}
        </${opts.interactive ? 'button' : 'span'}>`,
    )
    .join('')}</div>`;
}

export function chartCard(title: string, subtitle: string, body: string, extraClass = ''): string {
  return `<section class="card ${extraClass}">
    <div class="card-head"><h2>${title}</h2><p>${subtitle}</p></div>
    ${body}
  </section>`;
}

/**
 * Keep a segmented control's ARIA state in step with its class. `.active` is a
 * purely visual cue; without aria-pressed a screen-reader user cannot tell
 * which carrier or scope is currently selected.
 */
export function setPressed(buttons: Iterable<Element>, isOn: (el: Element) => boolean): void {
  for (const b of buttons) {
    const on = isOn(b);
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
}

export function emptyState(msg: string): string {
  return `<div class="empty">${esc(msg)}</div>`;
}

/**
 * Wire click AND keyboard activation on an element that carries role="button".
 * A real <button> activates on Enter and Space for free; an SVG <g> or <rect>
 * with role="button" does not, so announcing it as a button and handling only
 * `click` leaves it dead for anyone not using a mouse.
 */
export function onActivate(el: Element, fn: (e: Event) => void): void {
  el.addEventListener('click', fn);
  el.addEventListener('keydown', (e) => {
    const key = (e as KeyboardEvent).key;
    if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') return;
    e.preventDefault();
    fn(e);
  });
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Make a dialog behave like one: move focus in, keep Tab inside it, hide the
 * rest of the page from assistive technology, and put focus back where it was
 * on close. `aria-modal="true"` is a promise to do all of this — without it the
 * attribute is a lie and a screen-reader user simply keeps tabbing into the
 * page behind.
 */
export function trapFocus(dialog: HTMLElement, appRoot: HTMLElement | null): () => void {
  const previous = document.activeElement as HTMLElement | null;
  appRoot?.setAttribute('inert', '');
  appRoot?.setAttribute('aria-hidden', 'true');

  const focusables = () => [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((e) => e.offsetParent !== null || e === dialog);
  const first = focusables()[0];
  (first ?? dialog).focus?.();

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const head = items[0];
    const tail = items[items.length - 1];
    if (e.shiftKey && document.activeElement === head) {
      e.preventDefault();
      tail.focus();
    } else if (!e.shiftKey && document.activeElement === tail) {
      e.preventDefault();
      head.focus();
    }
  };
  dialog.addEventListener('keydown', onKey);

  return () => {
    dialog.removeEventListener('keydown', onKey);
    appRoot?.removeAttribute('inert');
    appRoot?.removeAttribute('aria-hidden');
    previous?.focus?.();
  };
}

/** Rounded-corner path for a horizontal bar, used by the ladder and slopes. */
export function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const el = document.createElementNS('http://www.w3.org/2000/svg', name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/** Nice round tick values covering [0, max]. */
export function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  return out;
}
